import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ASSETS } from "../lib/api";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useT();

  const NAV_LINKS = [
    { label: t("nav.ledger"), href: "#ledger", testid: "nav-ledger" },
    { label: t("nav.missions"), href: "#missions", testid: "nav-missions" },
    { label: t("nav.trading_posts"), href: "#trading-posts", testid: "nav-trading-posts" },
    { label: t("nav.legacy"), href: "#legacy", testid: "nav-legacy" },
    { label: "Contact", href: "/contact", testid: "nav-contact", external: true },
    { label: "Help", href: "/help", testid: "nav-help", external: true },
  ];

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (window.location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="bv-navbar"
      className="sticky top-0 z-50 w-full backdrop-blur-md"
      style={{
        background: "rgba(15, 29, 20, 0.94)",
        borderBottom: "3px solid #D4AF37",
        boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="h-12 w-14 overflow-hidden bg-white rounded-sm shrink-0 flex items-center justify-center p-1">
            <img src={ASSETS.logo} alt="Hayti Bon Sèvis Shipping" className="h-full w-full object-contain" />
          </div>
          <div className="leading-none hidden sm:block">
            <div className="font-serif-bv text-xl tracking-wider text-[#D4AF37]">HBS</div>
            <div className="text-[9px] tracking-[0.28em] text-[#E8C857]/70 uppercase mt-1">Hayti Bon Sèvis Shipping</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => handleAnchor(e, l.href)}
              data-testid={l.testid}
              className="text-sm tracking-[0.18em] uppercase text-[#F5EFE0] hover:text-[#D4AF37] transition-colors relative"
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-[#D4AF37] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher tone="dark" />
          <Link
            to="/commission"
            data-testid="nav-commission-btn"
            className="btn-gold rounded-sm px-5 py-2.5 text-xs hidden"
          >
            {t("nav.commission_btn")}
          </Link>
          <Link to="/login" data-testid="nav-login-btn" className="btn-gold rounded-sm px-5 py-2.5 text-xs">
            Sign in
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-[#D4AF37] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#D4AF37]/30"
          style={{ background: "rgba(15, 29, 20, 0.98)" }}
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => handleAnchor(e, l.href)}
                className="text-sm tracking-[0.18em] uppercase text-[#F5EFE0] hover:text-[#D4AF37]"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2">
              <LanguageSwitcher tone="dark" />
            </div>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="btn-gold rounded-sm px-5 py-2.5 text-xs text-center"
            >
              Sign in
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
